import {Card, openDeleteDialog} from 'platform/components';
import {Box} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import {isNil, mergeAll} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {useDeleteTransferOrderItemMutation, useGetTransferOrderQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
  useTenant,
} from '@dms/shared';
import {OverviewTotalPrice, useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {TransferOrderItemEdit, TransferOrderItemEditProps} from './TransferOrderItemEdit';

export function ListOfItemsTab() {
  const {transferOrderId} = useWarehouseParams();

  const dispatch = useDispatch();

  const tenant = useTenant();

  const formatCurrency = useFormatCurrency();

  const [editDialogProps, setEditDialogProps] = useState<TransferOrderItemEditProps | null>(null);

  const transferOrderQuery = useGetTransferOrderQuery(
    {transferOrderId},
    {skip: isNilOrEmpty(transferOrderId)}
  );

  const [deleteTransferOrderItem] = useDeleteTransferOrderItemMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    queryId,
    getColumnData,
    refreshData,
    deselectAll,
  }) => {
    match(actionKey)
      .with('redirectDetail', () => {
        const warehouseId = getColumnData<{key: string}>('warehouse')?.value.key;
        const articleId = getColumnData<string>('articleId')?.value;

        if (isNil(warehouseId)) {
          return logError('warehouseId is not defined');
        }

        if (isNil(articleId)) {
          return logError('articleId is not defined');
        }

        window.open(
          composePath(warehouseRoutes.articleDetailOverview, {
            params: {
              warehouseId,
              id: articleId,
            },
            isSearchParamsPreserved: false,
          }),
          '_blank'
        );
      })
      .with('edit', () => {
        const transferOrderItemId = isArray(rowId) ? rowId[0] : rowId;

        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
          // Invalidating manually onClose to keep dialog rowControl saving feature intact.
          dispatch(metadaApi.util.invalidateTags([{type: 'transferOrders', id: transferOrderId}]));
        };

        setEditDialogProps({
          transferOrderId,
          transferOrderItemId,
          dataQueryId: queryId,
          onClose: handleClose,
        });
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        openDeleteDialog({
          onConfirm: () =>
            deleteTransferOrderItem({transferOrderId, body: {transferOrderItemIds: ids}})
              .unwrap()
              .then(() => refreshData())
              .then(() => deselectAll())
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {transferOrderId}]),
    [transferOrderId]
  );

  const totalPriceWithoutVat = formatCurrency(
    transferOrderQuery.data?.totalPurchasePrice?.withoutVat?.amount ?? 0,
    transferOrderQuery.data?.totalPurchasePrice?.withoutVat?.currency ?? tenant.tenantCurrency,
    2
  );

  const totalPriceWithVat = formatCurrency(
    transferOrderQuery.data?.totalPurchasePrice?.withVat?.amount ?? 0,
    transferOrderQuery.data?.totalPurchasePrice?.withVat?.currency ?? tenant.tenantCurrency,
    2
  );

  return (
    <Card isFullHeight title={i18n.t('general.labels.listOfItems')}>
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {isNil(editDialogProps) ? null : <TransferOrderItemEdit {...editDialogProps} />}
      <Box height="95%">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.warehouseTransferOrderItems}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          predefinedColumns={PREDEFINED_COLUMNS}
        />
      </Box>
      <OverviewTotalPrice
        totalPriceLabel={i18n.t('general.labels.totalPrice')}
        totalPriceWithoutVat={totalPriceWithoutVat}
        totalPriceWithVat={totalPriceWithVat}
        data-testid="totalPrice"
      />
    </Card>
  );
}

const PREDEFINED_COLUMNS = ['warehouse', 'articleId'] as const;
